const AMOUNT_INPUT_CAP = 999999;

/**
 * Parses raw text-field input into a whole-pound amount.
 * Non-numeric characters are stripped (typing them has no effect);
 * an empty field is represented as 0.
 */
export function parseAmountInput(raw: string): number {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) return 0;
  return Math.min(AMOUNT_INPUT_CAP, parseInt(digits, 10));
}

/** Steps the amount up and snaps to the next multiple of `step`, clamped to [minimum, maximum]. */
export function snapAmountUp(
  amount: number,
  step: number,
  minimum: number,
  maximum: number,
): number {
  return Math.max(
    minimum,
    Math.min(maximum, Math.floor(amount / step) * step + step),
  );
}

/** Steps the amount down and snaps to the previous multiple of `step`, clamped to `minimum`. */
export function snapAmountDown(
  amount: number,
  step: number,
  minimum: number,
): number {
  return Math.max(minimum, Math.ceil(amount / step) * step - step);
}

export type AmountValidation =
  | { valid: true; errorTitle?: undefined; errorBody?: undefined }
  | { valid: false; errorTitle: string; errorBody?: string };

export function validateAmount(
  amount: number,
  minimum: number,
  maximum: number,
): AmountValidation {
  if (amount >= minimum && amount <= maximum) return { valid: true };

  const maxLabel = `£${maximum.toLocaleString('en-GB')}`;

  if (amount > maximum) {
    return {
      valid: false,
      errorTitle: `That is over the ${maxLabel} limit`,
      errorBody: `${maxLabel} is the most a child can receive across their Junior ISAs this tax year. Tap 'How much can I gift?' for the detail.`,
    };
  }
  if (amount > 0) {
    return { valid: false, errorTitle: `The minimum gift is £${minimum}` };
  }
  return { valid: false, errorTitle: 'Please enter an amount' };
}
